import { useState } from 'react';
import Button from '@components/forms/Button';

interface Props {
  emailDomain: string;
  autoJoin: boolean;
  existingDomainUserCount: number;
  isAdmin: boolean;
  onAutoJoinChange: (enabled: boolean) => Promise<void> | void;
}

const SettingsTab = ({ emailDomain, autoJoin, existingDomainUserCount, isAdmin, onAutoJoinChange }: Props) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleToggle = async () => {
    if (!isAdmin) return;
    if (!autoJoin) {
      setShowConfirm(true);
    } else {
      try {
        await onAutoJoinChange(false);
      } catch (error) {
        console.error('Error disabling domain auto-join:', error);
      }
    }
  };

  const handleConfirm = async () => {
    try {
      await onAutoJoinChange(true);
      setShowConfirm(false);
    } catch (error) {
      console.error('Error enabling domain auto-join:', error);
    }
  };

  return (
    <div className="pt-4">
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
        <p className="text-xs font-bold text-grey-400 uppercase tracking-wider mb-1 pb-2 border-b border-grey-200">
          Domain Auto-Join
        </p>
        <p className="text-xs text-grey-500 mt-3 mb-4">
          When enabled, any new user who registers with a <strong>@{emailDomain}</strong> email address is automatically added to this organisation as a Member.
        </p>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-grey-800">
              Automatically add <span className="font-semibold">@{emailDomain}</span> users
            </p>
            <p className="text-xs text-grey-500 mt-0.5">
              {autoJoin ? 'New registrations with this domain will be added as Members.' : 'New registrations with this domain will not be added automatically.'}
            </p>
          </div>
          <button
            type="button"
            disabled={!isAdmin}
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${autoJoin ? 'bg-secondary' : 'bg-grey-300'}`}
            role="switch"
            aria-checked={autoJoin}>
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${autoJoin ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-base font-bold text-grey-900 mb-2">Enable domain auto-join?</h2>
            <p className="text-sm text-grey-600 mb-4">
              {existingDomainUserCount > 0 ? (
                <>
                  This will immediately add{' '}
                  <span className="font-semibold text-grey-900">{existingDomainUserCount} existing {existingDomainUserCount === 1 ? 'user' : 'users'}</span>{' '}
                  with a <span className="font-semibold">@{emailDomain}</span> address as Members of this organisation. All future registrations with this domain will also be added automatically.
                </>
              ) : (
                <>
                  All future registrations with a <span className="font-semibold">@{emailDomain}</span> address will be added automatically as Members of this organisation.
                </>
              )}
            </p>
            <p className="text-xs text-grey-400 mb-6">
              Members can be removed individually at any time from the Members tab.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outlined"
                className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 bg-white"
                onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-secondary text-white normal-case font-semibold rounded-lg text-sm py-2 px-4 hover:bg-secondary-dark"
                onClick={handleConfirm}>
                {existingDomainUserCount > 0
                  ? `Add ${existingDomainUserCount} ${existingDomainUserCount === 1 ? 'Member' : 'Members'}`
                  : 'Enable auto-join'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
